import { useEffect, useMemo, useState } from 'react';
import {
  emptyRunTimeline,
  foldRunEvent,
  foldRunEvents,
  isTerminalPhase,
  phaseForTool,
  type RunEvent,
  type RunPhase,
  type RunTimeline,
} from '@repo/shared-types';
import { api, chatApi, kbSweepsApi, type RunEventRow, type RunLifecycleStatus } from './api';

/**
 * Watching one run from the browser.
 *
 * Two sources, one timeline. The stored events are fetched once so a run that finished last week
 * renders immediately and completely; the EventSource then attaches from that cursor and carries
 * everything still forming — reasoning arriving a phrase at a time, a tool call in flight, the
 * commit landing. Both go through `foldRunEvent` from `@repo/shared-types`, which is why a replayed
 * run and a live one cannot end up disagreeing about what the agent did.
 *
 * EventSource, not a hand-rolled fetch reader, on purpose: reconnection with `Last-Event-ID` resume
 * is exactly the hard part, the browser already implements it, and the session cookie travels with
 * it so there is no token to put in a URL. The one thing it does badly is stopping — a closed
 * connection is a failure to retry, forever — which is what the server's `end` event is for.
 *
 * One hook for both kinds of watchable run. The two agents differ in what they *do*, not in how they
 * are watched: same event vocabulary, same endpoint shape, same fold. `kind` only picks which pair of
 * URLs to call, and everything downstream of that — this hook, the timeline component, the breathing
 * indicators — is shared. Adding a third kind of run means one entry in `RUN_ENDPOINTS`.
 */

/** Which family of runs to watch. The endpoints differ; nothing else does. */
export type RunStreamKind = 'kb' | 'automation' | 'playground' | 'ask' | 'sweep' | 'chat';

const RUN_ENDPOINTS: Record<RunStreamKind, {
  events: (projectId: string, runId: string) => Promise<{
    runId: string;
    status: RunLifecycleStatus;
    phase: string | null;
    activity: string | null;
    events: RunEventRow[];
  }>;
  streamUrl: (projectId: string, runId: string, params: { after?: number }) => string;
}> = {
  kb: { events: (p, r) => api.kb.runEvents(p, r), streamUrl: (p, r, q) => api.kb.runStreamUrl(p, r, q) },
  // `orgAutomations` rather than `automations`: automations are addressed globally, and their run
  // detail already lives on the home project's route — the streaming endpoints sit beside it.
  automation: {
    events: (p, r) => api.orgAutomations.runEvents(p, r),
    streamUrl: (p, r, q) => api.orgAutomations.runStreamUrl(p, r, q),
  },
  // The project id is ignored: a playground rehearsal reads the whole hierarchy by design, so it has
  // no project to be addressed within. The hook's signature keeps it because the other two kinds need
  // it, and inventing a second signature for one caller would cost more than passing a constant.
  playground: {
    events: (_p, r) => api.playground.runEvents(r),
    streamUrl: (_p, r, q) => api.playground.runStreamUrl(r, q),
  },
  // The fourth kind, and the reversal of an earlier deviation: asks began with no durable half
  // (`useAskStream` below carries the reasoning), and grew one when it turned out people want to
  // read an ask back later — so the standard events-then-stream shape applies to them too now.
  ask: {
    events: (p, r) => api.kb.askRunEvents(p, r),
    streamUrl: (p, r, q) => api.kb.askStreamUrl(p, r, q),
  },
  // The fifth kind: the sweep agent reading the store. Addressed from the project it was started
  // from, like an ingestion run.
  sweep: {
    events: (p, r) => kbSweepsApi.events(p, r),
    streamUrl: (p, r, q) => kbSweepsApi.streamUrl(p, r, q),
  },
  // The sixth: a Slack chat turn. Addressed globally like a playground rehearsal — access is "did you
  // ask in this conversation", not project membership — so the project id is ignored.
  chat: {
    events: (_p, r) => chatApi.turnEvents(r),
    streamUrl: (_p, r, q) => chatApi.turnStreamUrl(r, q),
  },
};

export interface UseRunStreamResult {
  timeline: RunTimeline;
  /** True from the first paint until the stored timeline has arrived. */
  loading: boolean;
  /** True while an EventSource is attached and the run is still moving. */
  live: boolean;
  error: string | null;
}

export function useRunStream(
  projectId: string | null,
  runId: string | null,
  /**
   * The run's status as the list knows it. Used only to decide whether to open a stream at all —
   * there is no point attaching one to a run that finished, and the stored events are the whole
   * story for it.
   */
  status: RunLifecycleStatus | null,
  kind: RunStreamKind = 'kb',
): UseRunStreamResult {
  const endpoints = RUN_ENDPOINTS[kind];
  const [timeline, setTimeline] = useState<RunTimeline>(() => emptyRunTimeline());
  const [loading, setLoading] = useState(false);
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !runId) {
      setTimeline(emptyRunTimeline());
      setLoading(false);
      setLive(false);
      return;
    }

    let cancelled = false;
    let source: EventSource | null = null;

    // Reset before anything loads: opening a second run must never show the first one's trail.
    setTimeline(emptyRunTimeline(status === 'QUEUED' ? 'queued' : undefined));
    setError(null);
    setLoading(true);

    const attach = (from: number) => {
      if (cancelled) return;
      const es = new EventSource(endpoints.streamUrl(projectId, runId, { after: from }));
      source = es;
      setLive(true);

      es.addEventListener('run', (event) => {
        if (cancelled) return;
        try {
          const parsed = JSON.parse((event as MessageEvent<string>).data) as RunEvent;
          // The cursor lives on the timeline itself, and `foldRunEvent` uses it to tell a genuinely
          // new delta from one the wire redelivered — so folding into current state is the whole of
          // what has to happen here. Reconnect resume needs no cursor of ours either: the browser
          // sends `Last-Event-ID` from the frame ids.
          setTimeline((current) => foldRunEvent(current, parsed));
          if (parsed.type === 'status' && isTerminalPhase(parsed.phase)) setLive(false);
        } catch {
          // A frame this build can't parse is skipped rather than fatal: the stream is a view of
          // work that is happening regardless, and one unreadable event should not close it.
        }
      });

      // The server says when there is nothing more to send. Closing here is what stops EventSource
      // from reconnecting every few seconds for the rest of the session.
      es.addEventListener('end', () => {
        es.close();
        setLive(false);
      });

      es.onerror = () => {
        // Left open deliberately on a transient failure — EventSource reconnects on its own, with
        // `Last-Event-ID` set from the last frame's id, so the resume is exact. Only a connection
        // the browser has given up on is worth surfacing.
        if (es.readyState === EventSource.CLOSED) {
          setLive(false);
          setError('Lost the live connection to this run.');
        }
      };
    };

    endpoints
      .events(projectId, runId)
      .then((page) => {
        if (cancelled) return;
        const events = page.events.map(runEventFromRow);
        const folded = foldRunEvents(emptyRunTimeline(), events);
        // The row's own phase wins over the last event's when the two disagree: a worker killed
        // mid-run leaves a timeline whose final event says "reading" on a run the row calls FAILED,
        // and a header that insists it is still reading would be a lie the page never recovers from.
        if (page.status === 'FAILED') folded.phase = 'failed';
        else if (page.status === 'COMPLETED') folded.phase = 'done';
        else if (page.phase) folded.phase = page.phase as RunPhase;
        if (page.activity) folded.activity = page.activity;

        setTimeline(folded);
        setLoading(false);

        if (page.status === 'QUEUED' || page.status === 'PROCESSING') attach(folded.cursor);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setLoading(false);
        setError(e.message);
      });

    return () => {
      cancelled = true;
      source?.close();
      setLive(false);
    };
    // `status` is deliberately not a dependency: it changes under polling, and re-running this would
    // tear down a healthy stream and re-fetch the timeline every few seconds. It is read once, to
    // decide the starting phase and whether to attach at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, runId, kind]);

  return { timeline, loading, live, error };
}

/**
 * Watching one ask — the run kind with no row behind it.
 *
 * Deliberately not a fourth entry in `RUN_ENDPOINTS`: that shape starts from a durable snapshot
 * (`events()`) plus a status the list already knows, and an ask has neither. Nothing is stored, the
 * status would have to be reconstructed from the wire it gates, and a page reload loses the POST
 * whose response *is* the answer — so there is nothing a snapshot could recover that the page could
 * still use. The stream is the whole story: it replays from the wire's beginning on connect, the
 * browser's own `Last-Event-ID` covers a dropped connection mid-ask, and the server's `end` event
 * closes it (see `useRunStream` above for why that event exists).
 *
 * The hook keys on the URL, which carries the askId — a new ask is a new URL, which is what resets
 * the timeline without a second piece of state to keep in step.
 */
export function useAskStream(url: string | null): { timeline: RunTimeline; live: boolean; error: string | null } {
  const [timeline, setTimeline] = useState<RunTimeline>(() => emptyRunTimeline('starting'));
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTimeline(emptyRunTimeline('starting'));
    setError(null);
    if (!url) {
      setLive(false);
      return;
    }

    const es = new EventSource(url);
    setLive(true);

    es.addEventListener('run', (event) => {
      try {
        const parsed = JSON.parse((event as MessageEvent<string>).data) as RunEvent;
        setTimeline((current) => foldRunEvent(current, parsed));
        if (parsed.type === 'status' && isTerminalPhase(parsed.phase)) setLive(false);
      } catch {
        // One unreadable frame must not close a stream of work that is happening regardless.
      }
    });

    es.addEventListener('end', () => {
      es.close();
      setLive(false);
    });

    es.onerror = () => {
      // EventSource reconnects on its own with Last-Event-ID; only a connection the browser has
      // given up on is worth surfacing.
      if (es.readyState === EventSource.CLOSED) {
        setLive(false);
        setError('Lost the live view of this ask. The answer still arrives normally.');
      }
    };

    return () => {
      es.close();
      setLive(false);
    };
  }, [url]);

  return { timeline, live, error };
}

/**
 * A stored row back into the event it was — the browser-side mirror of the reader's `fromRow`.
 *
 * Duplicated rather than shared because the two sides receive different things: the API hands the
 * table's own shape to the browser (dates as strings, payload as parsed JSON), while the server
 * reads Prisma rows. Folding both into one `RunEvent` union here is what keeps the *rendering* path
 * single, which is the part that matters.
 */
export function runEventFromRow(row: RunEventRow): RunEvent {
  const base = { seq: row.seq, runId: row.runId, at: row.at, chunk: row.chunk };

  switch (row.type) {
    case 'thought':
      return {
        ...base,
        type: 'thought',
        id: row.eventId ?? `thought-${row.seq}`,
        kind: row.state === 'reasoning' ? 'reasoning' : 'narration',
        text: row.text ?? '',
        done: true,
      };
    case 'tool':
      return {
        ...base,
        type: 'tool',
        id: row.eventId ?? `tool-${row.seq}`,
        tool: row.label ?? 'unknown',
        state: row.state === 'refused' || row.state === 'error' ? row.state : 'ok',
        input: row.payload?.input,
        output: row.payload?.output,
        ...(row.ms !== null ? { ms: row.ms } : {}),
      };
    case 'milestone':
      return {
        ...base,
        type: 'milestone',
        label: row.label ?? '',
        ...(row.payload?.detail ? { detail: row.payload.detail } : {}),
        tone: row.state === 'warn' ? 'warn' : 'good',
      };
    default:
      return {
        ...base,
        type: 'status',
        phase: (row.phase as RunPhase) ?? 'queued',
        chunkCount: typeof row.payload?.chunkCount === 'number' ? row.payload.chunkCount : null,
        activity: row.label ?? null,
        ...(row.payload?.error ? { error: row.payload.error } : {}),
      };
  }
}

/**
 * What to put in front of a person as the single line describing right now.
 *
 * Derived from the tail of the timeline rather than from the phase alone, because a phase is a
 * category and this wants the specific: not "reading" but "reading engineering/auth/sessions". A
 * tool still running wins over anything settled — it is the thing actually taking the time.
 */
export function useLiveHeadline(timeline: RunTimeline): { phase: RunPhase; line: string } {
  return useMemo(() => {
    const running = [...timeline.activities].reverse().find((a) => a.kind === 'tool' && a.state === 'running');
    if (running && running.kind === 'tool') {
      return { phase: phaseForTool(running.tool), line: describeTool(running.tool, running.input) };
    }

    const streaming = [...timeline.activities].reverse().find((a) => a.kind === 'thought' && !a.done);
    if (streaming) return { phase: 'thinking', line: 'Working it out' };

    const last = timeline.activities[timeline.activities.length - 1];
    if (last?.kind === 'milestone') return { phase: timeline.phase, line: last.label };

    return { phase: timeline.phase, line: timeline.activity ?? '' };
  }, [timeline]);
}

/**
 * A tool call in the words of what it is actually doing.
 *
 * Worth the mapping: `kb_stage_update {"path":"engineering/auth"}` is what the agent called, but
 * "Rewriting engineering/auth" is what is happening, and the second is what someone watching a run
 * they did not start needs to see.
 *
 * One table for both agents' rosters, because the tool names don't collide and a call means the same
 * thing whichever loop made it — `kb_read` is reading a page in an ingestion run and in an automation
 * alike. The automation-only tools are the raw feeds, its own history, and the two hand-offs out.
 */
export function describeTool(tool: string, input: unknown): string {
  const args = (input ?? {}) as Record<string, unknown>;
  const days = typeof args.days === 'number' ? args.days : undefined;

  // Both shapes, because the two agents' rosters diverged deliberately. The ingestion and retrieval
  // loops take `paths`/`queries` so one turn can ask several things at once — a turn re-sends the
  // whole conversation, so N separate calls cost N re-reads of a growing prompt. The automation and
  // MCP tools stayed singular: their callers are external clients that batch on their own, and the
  // cost that motivated the change was measured on ingestion. One reader for both, since a call
  // means the same thing whichever loop made it.
  const first = (plural: unknown, single: unknown): { value?: string; extra: number } => {
    if (Array.isArray(plural)) {
      const strings = plural.filter((v): v is string => typeof v === 'string');
      return { value: strings[0], extra: Math.max(strings.length - 1, 0) };
    }
    return { value: typeof single === 'string' ? single : undefined, extra: 0 };
  };
  const andMore = (extra: number) => (extra > 0 ? ` and ${extra} more` : '');

  const read = first(args.paths, args.path);
  const path = read.value;
  const search = first(args.queries, args.query);
  const query = search.value;

  switch (tool) {
    // ── The automation agent's own tools ──
    case 'current_datetime':
      return 'Checking the date and what triggered it';
    case 'kb_ask':
      return typeof args.question === 'string' ? `Asking: ${truncate(args.question, 70)}` : 'Asking the knowledge base';
    case 'granola_list_notes':
      return days ? `Listing meeting notes from the last ${days} days` : 'Listing meeting notes';
    case 'granola_read_note':
      return 'Reading a meeting note in full';
    case 'slack_list_messages':
      return days ? `Reading Slack from the last ${days} days` : 'Reading Slack messages';
    case 'automation_previous_runs':
      return 'Looking back at its own previous runs';
    case 'slack_post_message':
      return typeof args.channelId === 'string' ? `Posting to ${args.channelId}` : 'Posting to Slack';
    case 'kb_save_note':
      return typeof args.title === 'string' ? `Filing “${truncate(args.title, 50)}” to the knowledge base` : 'Filing a note';
    case 'finish':
      return 'Writing the document';
    // ── The sweep agent ──
    case 'sweep_projects':
      return 'Reading what each project is for';
    case 'sweep_outline':
      return typeof args.projectId === 'string' ? 'Reading one project\'s tree' : 'Reading the shape of the whole store';
    case 'sweep_search':
      return query ? `Searching for “${query}”${andMore(search.extra)}` : 'Searching the store';
    case 'sweep_grep':
      return typeof args.pattern === 'string' ? `Scanning for “${truncate(args.pattern, 50)}”` : 'Scanning for an exact match';
    case 'sweep_read':
      return path ? `Reading ${path}${andMore(read.extra)}` : 'Reading a page';
    case 'sweep_similar_pairs':
      return 'Looking for pages that sit close together';
    case 'sweep_neighbours':
      return path ? `Looking at what sits next to ${path}` : 'Looking at a page\'s neighbours';
    case 'sweep_compare':
      return typeof args.pathA === 'string' && typeof args.pathB === 'string' ? `Comparing ${truncate(args.pathA, 40)} with ${truncate(args.pathB, 40)}` : 'Comparing two pages';
    case 'sweep_shared_evidence':
      return 'Looking for source sentences cited on several pages';
    case 'sweep_open_findings':
      return 'Re-reading the cards earlier sweeps left open';
    case 'sweep_flag':
      return typeof args.title === 'string' ? `Flagging: ${truncate(args.title, 60)}` : 'Flagging a finding';
    case 'sweep_resolve':
      return typeof args.id === 'string' ? `Withdrawing ${args.id}` : 'Withdrawing a card';
    case 'sweep_finish':
      return 'Writing the report';
    case 'kb_outline':
      return 'Reading the shape of the knowledge base';
    case 'kb_search':
      return query ? `Searching for “${query}”${andMore(search.extra)}` : 'Searching the knowledge base';
    case 'kb_grep':
      return 'Scanning for an exact match';
    case 'kb_read':
      return path ? `Reading ${path}${andMore(read.extra)}` : 'Reading a page';
    case 'kb_stage_create':
      return path ? `Drafting a new page at ${path}` : 'Drafting a new page';
    case 'kb_stage_update':
      return path ? `Rewriting ${path}` : 'Rewriting a page';
    case 'kb_stage_delete':
      return path ? `Proposing to remove ${path}` : 'Proposing a removal';
    case 'kb_stage_merge':
      return 'Merging two pages';
    case 'kb_stage_move':
      return path ? `Moving ${path}` : 'Moving a page';
    case 'kb_review_plan':
      return 'Checking its own plan';
    case 'kb_discard_staged':
      return 'Dropping a staged edit';
    case 'kb_commit':
      return 'Applying its changes';
    default:
      return tool;
  }
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}
